/**
 * withCostTracking — COST-1
 *
 * Wraps every external API call with:
 *   1. Pre-call cap check: reads cost_caps_current/{tenantId} and compares against
 *      tenant costCaps config. Throws COST_CAP_EXCEEDED if daily or monthly cap would
 *      be exceeded.
 *   2. Timed execution of the wrapped fn().
 *   3. Post-call cost_events write (success or failure path).
 *   4. Atomic increment of cost_caps_current running totals.
 *
 * Architecture rule (COST-1): This wrapper is additive — it wraps governedXCall,
 * it does NOT replace it. Wrapper chain: withCostTracking → governedXCall → SDK.
 *
 * Callers must NOT use // kairos-cost-exempt unless there is a documented reason.
 */

import DataService from '../DataService.js';
import { getContext } from '../obs/context.js';
import { createLogger } from '../obs/logger.js';

const log = createLogger('withCostTracking');

const COLLECTION = 'cost_events';
const CAPS_COLLECTION = 'cost_caps_current';

const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

function generateUlid() {
  let t = Date.now();
  const tc = new Array(10);
  for (let i = 9; i >= 0; i--) { tc[i] = CROCKFORD[t % 32]; t = Math.floor(t / 32); }
  const rnd = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(rnd);
  } else {
    rnd.forEach((_, i, a) => { a[i] = Math.floor(Math.random() * 256); });
  }
  return tc.join('') + Array.from(rnd, b => CROCKFORD[b & 0x1f]).join('');
}

function todayKey() {
  return new Date().toISOString().slice(0, 10); // YYYY-MM-DD
}

function monthKey() {
  return new Date().toISOString().slice(0, 7); // YYYY-MM
}

async function _readCapsConfig(tenantId) {
  try {
    const doc = await DataService.get('tenants', tenantId);
    return doc?.costCaps || null;
  } catch (err) {
    log.warn({ tenantId, err: err.message }, 'withCostTracking: failed to read costCaps config (non-fatal)');
    return null;
  }
}

async function _readCurrentUsage(tenantId) {
  try {
    const doc = await DataService.get(CAPS_COLLECTION, tenantId);
    if (!doc) return { daily: {}, monthly: {}, dailyDate: null, monthlyDate: null };
    const today = todayKey();
    const month = monthKey();
    return {
      daily: doc.dailyDate === today ? (doc.daily || {}) : {},
      monthly: doc.monthlyDate === month ? (doc.monthly || {}) : {},
      dailyDate: doc.dailyDate,
      monthlyDate: doc.monthlyDate,
    };
  } catch (err) {
    log.warn({ tenantId, err: err.message }, 'withCostTracking: failed to read current usage (non-fatal, allowing call)');
    return { daily: {}, monthly: {}, dailyDate: null, monthlyDate: null };
  }
}

function _checkCap({ capsConfig, usage, provider, estimateCostCents }) {
  if (!capsConfig?.enabled) return;
  const stopAt = capsConfig.hardStopAtPercent ?? 100;

  const dailyCap = capsConfig.daily?.[provider] ?? capsConfig.daily?.default ?? null;
  const monthlyCap = capsConfig.monthly?.[provider] ?? capsConfig.monthly?.default ?? null;
  const dailyUsed = usage.daily?.[provider] ?? 0;
  const monthlyUsed = usage.monthly?.[provider] ?? 0;

  if (dailyCap !== null) {
    const effectiveCap = Math.floor(dailyCap * stopAt / 100);
    if (dailyUsed + estimateCostCents > effectiveCap) {
      const err = new Error(`COST_CAP_EXCEEDED: daily cap for ${provider}`);
      err.code = 'COST_CAP_EXCEEDED';
      err.details = {
        provider,
        period: 'daily',
        capCents: dailyCap,
        effectiveCapCents: effectiveCap,
        currentCents: dailyUsed,
        estimateCents: estimateCostCents,
        periodStart: todayKey(),
      };
      throw err;
    }
  }

  if (monthlyCap !== null) {
    const effectiveCap = Math.floor(monthlyCap * stopAt / 100);
    if (monthlyUsed + estimateCostCents > effectiveCap) {
      const err = new Error(`COST_CAP_EXCEEDED: monthly cap for ${provider}`);
      err.code = 'COST_CAP_EXCEEDED';
      err.details = {
        provider,
        period: 'monthly',
        capCents: monthlyCap,
        effectiveCapCents: effectiveCap,
        currentCents: monthlyUsed,
        estimateCents: estimateCostCents,
        periodStart: monthKey(),
      };
      throw err;
    }
  }
}

async function _writeCostEvent({ tenantId, provider, model, purpose, costCents, outcome, durationMs, correlationId, callerSession, agentId, agentType }) {
  const costEventId = generateUlid();
  const today = todayKey();
  const month = monthKey();
  await DataService.create(COLLECTION, {
    costEventId,
    tenantId,
    provider,
    model: model || null,
    purpose,
    costCents,
    outcome,
    durationMs,
    correlationId: correlationId || null,
    callerSession: callerSession || null,
    // COST-1-EXPANSION: per-agent attribution fields
    agentId: agentId || null,
    agentType: agentType || null,
    softDeleted: null,
  }, 'system', 'cost-tracking');

  // Update running totals in cost_caps_current — best-effort (non-blocking)
  _incrementRunningTotals(tenantId, provider, costCents, today, month).catch(err => {
    log.warn({ tenantId, provider, err: err.message }, 'withCostTracking: running total increment failed (non-fatal)');
  });

  return costEventId;
}

async function _incrementRunningTotals(tenantId, provider, costCents, today, month) {
  const existing = await DataService.get(CAPS_COLLECTION, tenantId);
  const resetDaily = !existing || existing.dailyDate !== today;
  const resetMonthly = !existing || existing.monthlyDate !== month;

  const update = {
    tenantId,
    dailyDate: today,
    monthlyDate: month,
    [`daily.${provider}`]: resetDaily ? costCents : (existing?.daily?.[provider] ?? 0) + costCents,
    [`daily.total`]: resetDaily ? costCents : (existing?.daily?.total ?? 0) + costCents,
    [`monthly.${provider}`]: resetMonthly ? costCents : (existing?.monthly?.[provider] ?? 0) + costCents,
    [`monthly.total`]: resetMonthly ? costCents : (existing?.monthly?.total ?? 0) + costCents,
  };

  if (existing) {
    await DataService.update(CAPS_COLLECTION, tenantId, update, 'system');
  } else {
    await DataService.set(CAPS_COLLECTION, tenantId, { ...update, daily: { [provider]: costCents, total: costCents }, monthly: { [provider]: costCents, total: costCents } }, 'system', 'cost-tracking');
  }
}

/**
 * withCostTracking — main entry point.
 *
 * @param {object} opts
 * @param {string}  opts.provider          - 'anthropic'|'openai'|'easypost'|'twilio'|'sendgrid'|'bland'|'mapbox'
 * @param {string|null} opts.model         - model ID or null for non-LLM calls
 * @param {string}  opts.purpose           - purpose slug ('tender','track','reply',...)
 * @param {string}  opts.tenantId          - tenant identifier
 * @param {string}  [opts.correlationId]   - OBS-1 correlation id; falls back to context
 * @param {number}  [opts.estimateCostCents] - pre-call estimate for cap check (default 0)
 * @param {string}  [opts.agentId]         - COST-1-EXPANSION: agent ID (e.g. 'AEGIS-DRAY', 'ARTEMIS-3D', 'ARGUS-MAIN')
 * @param {string}  [opts.agentType]       - COST-1-EXPANSION: agent class ('aegis'|'artemis'|'argus'|'council'|'batch')
 * @param {Function} fn                    - async function to wrap
 * @returns {Promise<*>} the result of fn()
 */
export async function withCostTracking({ provider, model = null, purpose, tenantId, correlationId, estimateCostCents = 0, agentId = null, agentType = null }, fn) {
  const ctx = getContext();
  const resolvedCid = correlationId || ctx.correlationId || null;
  const callerSession = ctx.sessionId || null;
  const resolvedAgentId = agentId || ctx.agentId || null;
  const resolvedAgentType = agentType || ctx.agentType || null;
  const today = todayKey();
  const month = monthKey();

  // Pre-call: cap check
  let capsConfig = null;
  try {
    capsConfig = await _readCapsConfig(tenantId);
    if (capsConfig?.enabled && estimateCostCents > 0) {
      const usage = await _readCurrentUsage(tenantId);
      _checkCap({ capsConfig, usage, provider, estimateCostCents });
    }
  } catch (err) {
    if (err.code === 'COST_CAP_EXCEEDED') {
      // Still write a cost_events record for the blocked attempt
      await _writeCostEvent({
        tenantId, provider, model, purpose,
        costCents: 0,
        outcome: 'rate_limited',
        durationMs: 0,
        correlationId: resolvedCid,
        callerSession,
        agentId: resolvedAgentId,
        agentType: resolvedAgentType,
      }).catch(() => {});
      throw err;
    }
    // Non-cap errors reading config are non-fatal — allow call through
    log.warn({ tenantId, err: err.message }, 'withCostTracking: cap check error (allowing call)');
  }

  const start = Date.now();
  let outcome = 'success';
  let actualCostCents = estimateCostCents;
  let result;

  try {
    result = await fn();
    // If result carries token usage, compute actual cost
    if (result && typeof result === 'object') {
      const tokens = result.usage || result.tokenUsage || null;
      if (tokens) {
        actualCostCents = _estimateFromTokens(provider, model, tokens) ?? estimateCostCents;
      }
    }
  } catch (err) {
    outcome = _classifyError(err);
    const durationMs = Date.now() - start;
    await _writeCostEvent({
      tenantId, provider, model, purpose,
      costCents: estimateCostCents,
      outcome,
      durationMs,
      correlationId: resolvedCid,
      callerSession,
      agentId: resolvedAgentId,
      agentType: resolvedAgentType,
    }).catch(writeErr => {
      log.warn({ tenantId, err: writeErr.message }, 'withCostTracking: cost_events write failed on error path');
    });
    throw err;
  }

  const durationMs = Date.now() - start;
  await _writeCostEvent({
    tenantId, provider, model, purpose,
    costCents: actualCostCents,
    outcome,
    durationMs,
    correlationId: resolvedCid,
    callerSession,
    agentId: resolvedAgentId,
    agentType: resolvedAgentType,
  }).catch(writeErr => {
    log.warn({ tenantId, err: writeErr.message }, 'withCostTracking: cost_events write failed on success path');
  });

  return result;
}

function _classifyError(err) {
  const msg = err?.message || '';
  if (err?.status === 429 || msg.includes('rate limit') || msg.includes('429')) return 'rate_limited';
  if (err?.status === 504 || msg.includes('timeout') || msg.includes('ETIMEDOUT')) return 'timeout';
  return 'failure';
}

// Provider cost tables (cents per 1k tokens or per call).
const TOKEN_COST_TABLE = {
  'anthropic': {
    'claude-opus-4-7':    { input: 1.5, output: 7.5 },   // $15/$75 per 1M
    'claude-sonnet-4-6':  { input: 0.3, output: 1.5 },
    'claude-haiku-4-5-20251001': { input: 0.025, output: 0.125 },
    default:              { input: 0.3, output: 1.5 },
  },
  'openai': {
    'gpt-4o':    { input: 0.25, output: 1.0 },
    'gpt-4o-mini': { input: 0.015, output: 0.06 },
    default:     { input: 0.25, output: 1.0 },
  },
};

function _estimateFromTokens(provider, model, usage) {
  const table = TOKEN_COST_TABLE[provider];
  if (!table) return null;
  const rates = table[model] || table.default;
  if (!rates) return null;
  const inputTokens = usage.input_tokens || usage.prompt_tokens || 0;
  const outputTokens = usage.output_tokens || usage.completion_tokens || 0;
  // Rates are in cents per 1k tokens
  return Math.round((inputTokens / 1000) * rates.input + (outputTokens / 1000) * rates.output);
}

export default withCostTracking;
